import LanguageIcon from '@mui/icons-material/Language';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material';
import {deleteObject, ref} from 'firebase/storage';
import {useCallback, useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {useStorage} from 'reactfire';
import {CloseButton} from '../common/button/CloseButton';
import {detectLocale} from '../i18n/hooks/useLocale';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {DefaultLocale, SUPPORTED_LOCALES} from '../i18n/locales';
import {Translation} from '../i18n/Translation';
import {AutocompleteInputField} from '../input/AutocompleteInputField';
import {DialogForm, useDialogForm} from '../input/DialogForm';
import {SaveButton} from '../input/SaveButton';
import {SingleChoiceToggleButtonInputField} from '../input/SingleChoiceToggleButtonInputField';
import {TextInputField} from '../input/TextInputField';
import {ComponentWithoutChildren} from '../react/types';
import {ResponsiveDialogProps} from '../responsive/ResponsiveDialog';
import {useThemeName} from '../theme/hooks/useThemeName';
import {ThemeToggle} from '../theme/ThemeToggle';
import {EditAvatarBadge} from './EditAvatarBadge';
import {useCurrentUser} from './hooks/useCurrentUser';
import {EditUserFormValues, useEditUserSchema} from './hooks/useUserSchemas';
import {nameToInitials} from './UserAvatar';
import {logger} from '#common/logging/logger';
import {User} from '#common/users/types';

type AllowedDialogProps = Omit<ResponsiveDialogProps, 'onClose' | 'onInvalid' | 'onSubmit'>;
export interface EditUserDialogProps extends AllowedDialogProps {
  handleClose: () => void;
}

const UserAvatarPreview: ComponentWithoutChildren = () => {
  const {watch} = useFormContext<EditUserFormValues>();
  const avatar = watch('avatar');
  const name = watch('name') || '';
  const initials = useMemo(() => nameToInitials(name), [name]);

  return (
    <Avatar alt={name} slotProps={{img: {referrerPolicy: 'no-referrer'}}} src={avatar?.url}>
      {initials}
    </Avatar>
  );
};

/* v8 ignore start */
const getLocaleLabel = (code: string): string => {
  try {
    const nativeNames = new Intl.DisplayNames([code], {type: 'language'});
    const englishNames = new Intl.DisplayNames(['en'], {type: 'language'});
    const nativeName = nativeNames.of(code);
    const englishName = englishNames.of(code);
    const label =
      nativeName && englishName && nativeName !== englishName ? `${nativeName} (${englishName})` : nativeName || code;
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch (_error) {
    return code;
  }
};
/* v8 ignore stop */

interface EditUserFormContentProps {
  defaultValues: EditUserFormValues;
  userData: User;
}

const EditUserFormContent: ComponentWithoutChildren<EditUserFormContentProps> = ({
  defaultValues,
  userData,
}: EditUserFormContentProps) => {
  const {onAttemptClose} = useDialogForm();
  const {translate} = useTranslation('user');
  const {setValue, watch} = useFormContext<EditUserFormValues>();

  const watchLocaleMode = watch('localeMode');
  const watchName = watch('name') || '';

  useEffect(() => {
    if (watchLocaleMode === 'auto') {
      const detectedCode = detectLocale() ?? DefaultLocale.code;
      const fallbackLocale = SUPPORTED_LOCALES.includes(detectedCode) ? detectedCode : 'en-US';
      setValue('manualLocale', fallbackLocale);
    }
  }, [setValue, watchLocaleMode]);

  const getOptionLabel = useCallback((option: string) => {
    return getLocaleLabel(option);
  }, []);

  /* v8 ignore next 3 -- @preserve */
  const isOptionEqualToValue = useCallback((option: string, value: string) => {
    return option === value;
  }, []);

  return (
    <>
      <DialogTitle>
        <Stack direction="row" spacing="1rem" sx={{alignItems: 'center'}}>
          <EditAvatarBadge>
            <UserAvatarPreview />
          </EditAvatarBadge>
          <Stack direction="column">
            <span>{watchName}</span>
            <Typography variant="subtitle2">{userData.email}</Typography>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Profile Section */}
          <Stack spacing={2}>
            <Stack direction="row" spacing="0.5rem" sx={{alignItems: 'center'}}>
              <PersonIcon color="action" />
              <Typography
                sx={{color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase'}}
                variant="subtitle2"
              >
                {translate('user:section.profile')}
              </Typography>
            </Stack>
            <Translation keys={['user:field.name.label', 'user:field.name.helperText']}>
              {([label, helperText]) => <TextInputField fullWidth helperText={helperText} label={label} name="name" />}
            </Translation>
          </Stack>

          <Divider />

          {/* Appearance Section */}
          <Stack spacing={1}>
            <Stack direction="row" spacing="0.5rem" sx={{alignItems: 'center', mb: 1}}>
              <PaletteIcon color="action" />
              <Typography
                sx={{color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase'}}
                variant="subtitle2"
              >
                {translate('user:section.appearance')}
              </Typography>
            </Stack>
            <ThemeToggle name="preferences.theme" />
          </Stack>

          <Divider />

          {/* Language & Regional Section */}
          <Stack spacing={2}>
            <Stack direction="row" spacing="0.5rem" sx={{alignItems: 'center'}}>
              <LanguageIcon color="action" />
              <Typography
                sx={{color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase'}}
                variant="subtitle2"
              >
                {translate('user:section.language')}
              </Typography>
            </Stack>

            <SingleChoiceToggleButtonInputField defaultValue={defaultValues.localeMode} fullWidth name="localeMode">
              <ToggleButton value="auto">{translate('user:field.locale.mode.auto')}</ToggleButton>
              <ToggleButton value="manual">{translate('user:field.locale.mode.manual')}</ToggleButton>
            </SingleChoiceToggleButtonInputField>

            <AutocompleteInputField
              disableClearable
              disabled={watchLocaleMode === 'auto'}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              label={translate('user:field.locale.autocomplete.label')}
              name="manualLocale"
              options={SUPPORTED_LOCALES}
              required={watchLocaleMode === 'manual'}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <CloseButton onClick={onAttemptClose} />
        <SaveButton />
      </DialogActions>
    </>
  );
};

export const EditUserDialog: ComponentWithoutChildren<EditUserDialogProps> = ({
  handleClose,
  ...dialogProps
}: EditUserDialogProps) => {
  const {setThemeName, themeName: currentThemeName} = useThemeName();
  const user = useCurrentUser();
  const userData = user.get();
  const storage = useStorage();
  const editUserSchema = useEditUserSchema();

  /* v8 ignore start */
  const userTheme = userData.preferences?.theme || 'system';
  const handleCloseWithThemeRevert = useCallback(() => {
    // If the user changed the theme and then decides not to save it, revert to the old theme
    if (currentThemeName !== userTheme) {
      setThemeName(userTheme);
    }
    handleClose();
  }, [currentThemeName, handleClose, setThemeName, userTheme]);
  /* v8 ignore stop */

  /* v8 ignore start */
  const defaultValues = useMemo(() => {
    const initialLocale = userData.preferences?.locale;
    const detectedCode = detectLocale() ?? DefaultLocale.code;
    return {
      ...userData,
      localeMode: initialLocale ? ('manual' as const) : ('auto' as const),
      manualLocale: initialLocale || (SUPPORTED_LOCALES.includes(detectedCode) ? detectedCode : 'en-US'),
      preferences: {
        ...userData.preferences,
        theme: userData.preferences?.theme || 'system',
      },
    };
  }, [userData]);
  /* v8 ignore stop */

  const handleSubmit = useCallback(
    async (values: EditUserFormValues) => {
      const oldAvatar = userData.avatar;
      const newAvatar = values.avatar;
      /* v8 ignore next 8 -- @preserve */
      if (oldAvatar?.source === 'user' && oldAvatar.url !== newAvatar?.url) {
        try {
          const oldAvatarRef = ref(storage, oldAvatar.url);
          await deleteObject(oldAvatarRef);
        } catch (error) {
          logger.error('Failed to delete old avatar', {error});
        }
      }

      const preferences: Record<string, unknown> = {};
      if (values.localeMode === 'manual') {
        preferences.locale = values.manualLocale;
      }
      preferences.theme = values.preferences.theme;

      const update: Partial<User> = {
        avatar: values.avatar,
        name: values.name,
        preferences,
      };

      await user.update(update);
      handleClose();
    },
    [handleClose, storage, user, userData.avatar],
  );

  return (
    <DialogForm
      {...dialogProps}
      defaultValues={defaultValues}
      handleClose={handleCloseWithThemeRevert}
      onSubmit={handleSubmit}
      schema={editUserSchema}
    >
      <EditUserFormContent defaultValues={defaultValues} userData={userData} />
    </DialogForm>
  );
};

export default EditUserDialog;
